import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import WizardProgressIndicator from "components/ui/WizardProgressIndicator";
import Icon from "components/AppIcon";
import MultiStepForm from "./components/MultiStepForm";
import { useConfig } from "../../context/ConfigContext";

const MaterialInputWizard = () => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Standard Process inputs - these will be carried forward
    materialName: "",
    units: "",
    materialWeight: "",
    // Donation Model inputs - only COGS and tax rate
    looptCogsPerUnit: "",
    looptCorporateTaxRate: config?.default_tax_rate?.toString() || "20",
    // Rag Trader and Value Channel no longer need separate weight inputs
    // They will use the materialWeight and units from standard process
  });
  const [errors, setErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const steps = [
    {
      id: 1,
      title: "Standard Process",
      description: "Basic material inputs for standard processing",
      fields: ["materialName", "units", "materialWeight"],
    },
    {
      id: 2,
      title: "Donation Model",
      description: "Enhanced inputs for sustainable processing",
      fields: ["looptCogsPerUnit", "looptCorporateTaxRate"],
    },
    {
      id: 3,
      title: "Rag Trader",
      description: "Calculate revenue potential from rag trader channel",
      fields: [], // No input fields, just display calculations
    },
    {
      id: 4,
      title: "Value Channel",
      description: "Calculate revenue potential from value channel",
      fields: [], // No input fields, just display calculations
    },
  ];

  const currentStepData = steps.find((step) => step.id === currentStep);

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case "materialName":
        if (!value.trim()) return "Material name is required";
        if (value.trim().length < 2)
          return "Material name must be at least 2 characters";
        return "";

      case "units":
        if (!value) return "Units quantity is required";
        const unitsNum = parseFloat(value);
        if (isNaN(unitsNum) || unitsNum <= 0)
          return "Units must be a number greater than 0";
        return "";

      case "materialWeight":
        if (!value) return "Material weight is required";
        const weightNum = parseFloat(value);
        if (isNaN(weightNum) || weightNum <= 0)
          return "Weight must be a number greater than 0";
        return "";

      case "looptCogsPerUnit":
        if (!value) return "COGS per unit is required";
        const cogsNum = parseFloat(value);
        if (isNaN(cogsNum) || cogsNum <= 0)
          return "COGS must be a number greater than 0";
        return "";

      case "looptCorporateTaxRate":
        if (!value) return "Corporate tax rate is required";
        const taxNum = parseFloat(value);
        if (isNaN(taxNum) || taxNum < 0 || taxNum > 100)
          return "Tax rate must be between 0 and 100";
        return "";

      default:
        return "";
    }
  };

  // Handle input changes with real-time validation
  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Real-time validation
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Validate current step
  const validateCurrentStep = () => {
    const newErrors = {};
    currentStepData.fields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));
    return Object.keys(newErrors).length === 0;
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check current step validity
  useEffect(() => {
    const currentFields = currentStepData.fields;
    const hasAllCurrentFields = currentFields.every(
      (field) => formData[field]?.toString().trim() !== ""
    );
    const hasNoCurrentErrors = currentFields.every((field) => !errors[field]);
    setIsFormValid(hasAllCurrentFields && hasNoCurrentErrors);
  }, [formData, errors, currentStep, currentStepData]);

  // Step navigation functions
  const handleStepNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      } else {
        handleFinalNext(); // Final submission
      }
    }
  };

  const handleStepPrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate("/welcome-calculator-introduction");
    }
  };

  // Handle final navigation when all steps are complete
  const handleFinalNext = async () => {
    if (!validateForm()) return;

    setIsValidating(true);

    // Simulate validation processing
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Store form data in localStorage for next steps
    localStorage.setItem("materialInputData", JSON.stringify(formData));

    setIsValidating(false);

    // Navigate to calculation processing with the form data
    navigate("/calculation-processing", {
      state: {
        inputData: formData,
        timestamp: new Date().toISOString(),
      },
    });
  };

  // Load saved data on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("materialInputData");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      } catch (error) {
        console.error("Error loading saved data:", error);
      }
    }
  }, []);

  // Update default tax rate when config loads
  useEffect(() => {
    if (config && !localStorage.getItem("materialInputData")) {
      setFormData((prev) => ({
        ...prev,
        looptCorporateTaxRate: config.default_tax_rate?.toString() || "20",
      }));
    }
  }, [config]);

  return (
    <div className="min-h-screen">
      <WizardProgressIndicator />

      <main className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <Icon name="Package" size={24} className="text-primary" />
              </div>
              <h1 className="text-3xl font-heading-bold text-text-primary">
                Material Input Wizard
              </h1>
            </div>
          </div>

          {/* Main Content - Centered */}
          <div className="max-w-4xl mx-auto">
            {/* Form Section */}
            <div className="bg-surface rounded-lg border border-border shadow-elevation-1">
              <div className="p-6 border-b border-border">
                <div className="flex items-center space-x-3">
                  <Icon name="Edit3" size={20} className="text-primary" />
                  <h2 className="text-xl font-heading-semibold text-text-primary">
                    Material Specifications
                  </h2>
                </div>
                <p className="mt-2 text-sm text-text-secondary">
                  Provide accurate material details for precise calculations
                </p>
              </div>

              <div className="p-6">
                <MultiStepForm
                  currentStep={currentStep}
                  steps={steps}
                  formData={formData}
                  errors={errors}
                  onInputChange={handleInputChange}
                  isValidating={isValidating}
                  onStepNext={handleStepNext}
                  onStepPrevious={handleStepPrevious}
                  isFormValid={isFormValid}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MaterialInputWizard;
